/**
 * EventGenerator - Creates telegraphed events with 3-layer structure
 * 
 * Events are FORCES, not enemies
 * They are telegraphed before hitting (user can see them coming)
 * 
 * 3 Layers:
 * 1. Systemic force (dark core) - unavoidable
 * 2. Collective amplification (unstable mid-layer) - partially mitigable
 * 3. Personal exposure (outer layer) - user choices matter
 */

export class EventGenerator {
  constructor(config = {}) {
    this.eventQueue = [];
    this.nextEventTime = 0;
    this.currentTime = 0;
    
    // Event frequency curves (events per minute)
    this.frequencies = {
      routine: 0.5,        // Frequent, low intensity
      systemic: 0.1,       // Moderate frequency, pressure events
      shock: 0.02          // Rare, high impact
    };
    
    // Difficulty ramps over time
    this.difficultyMultiplier = 1.0;
  }

  update(deltaTime, lives = []) {
    this.currentTime += deltaTime;
    
    // Increase difficulty over time (subtle)
    this.difficultyMultiplier = 1.0 + (this.currentTime / 300) * 0.5; // +50% after 5 minutes
    
    // Check if it's time to generate a new event
    if (this.currentTime >= this.nextEventTime) {
      this.generateEvent(lives);
      this.scheduleNextEvent();
    }
    
    // Update event queue (telegraphing)
    this.eventQueue = this.eventQueue.filter(event => {
      event.timeUntilImpact -= deltaTime;
      
      if (event.timeUntilImpact <= 0) {
        // Event hits - send to target life
        if (event.targetLife) {
          event.targetLife.receiveEvent(event);
        }
        return false; // Remove from queue
      }
      
      return true; // Still telegraphing
    });
    
    return this.eventQueue;
  }

  generateEvent(lives) {
    // Determine event type based on frequency
    const roll = Math.random();
    let eventType;
    
    if (roll < 0.7) {
      eventType = this.createRoutineEvent();
    } else if (roll < 0.95) {
      eventType = this.createSystemicEvent();
    } else {
      eventType = this.createShockEvent();
    }
    
    // Select random target (or multiple for systemic events)
    const targetCount = eventType.category === 'systemic' ? Math.min(3, lives.length) : 1;
    const targets = this.selectRandomLives(lives, targetCount);
    
    targets.forEach(life => {
      const event = {
        ...eventType,
        id: `event-${Date.now()}-${Math.random()}`,
        targetLife: life,
        timeUntilImpact: 3.0, // 3 second telegraph
        createdAt: this.currentTime
      };
      
      this.eventQueue.push(event);
    });
  }

  createRoutineEvent() {
    const routineEvents = [
      {
        name: 'Fender Bender',
        zone: 'auto',
        category: 'routine',
        baseDamage: 10 * this.difficultyMultiplier,
        description: 'Minor traffic collision',
        visual: {
          color: '#FF6B6B',
          intensity: 0.3,
          particleCount: 20
        },
        layers: {
          systemic: 0.2,      // 20% unavoidable
          collective: 0.3,     // 30% collective
          personal: 0.5        // 50% user-mitigable
        }
      },
      {
        name: 'Minor Illness',
        zone: 'health',
        category: 'routine',
        baseDamage: 8 * this.difficultyMultiplier,
        description: 'Seasonal illness',
        visual: {
          color: '#4ECDC4',
          intensity: 0.3,
          particleCount: 15
        },
        layers: {
          systemic: 0.3,
          collective: 0.2,
          personal: 0.5
        }
      },
      {
        name: 'Appliance Failure',
        zone: 'home',
        category: 'routine',
        baseDamage: 12 * this.difficultyMultiplier,
        description: 'HVAC or plumbing issue',
        visual: {
          color: '#FFE66D',
          intensity: 0.3,
          particleCount: 25
        },
        layers: {
          systemic: 0.1,
          collective: 0.2,
          personal: 0.7
        }
      }
    ];
    
    return routineEvents[Math.floor(Math.random() * routineEvents.length)];
  }

  createSystemicEvent() {
    const systemicEvents = [
      {
        name: 'Medical Cost Surge',
        zone: 'health',
        category: 'systemic',
        baseDamage: 20 * this.difficultyMultiplier,
        description: 'Healthcare system pressure',
        visual: {
          color: '#A8DADC',
          intensity: 0.6,
          particleCount: 50,
          pulseSpeed: 0.5
        },
        layers: {
          systemic: 0.6,      // Mostly unavoidable
          collective: 0.3,
          personal: 0.1
        },
        duration: 5.0 // Longer duration
      },
      {
        name: 'Material Cost Spike',
        zone: 'home',
        category: 'systemic',
        baseDamage: 18 * this.difficultyMultiplier,
        description: 'Construction material shortage',
        visual: {
          color: '#F4A261',
          intensity: 0.6,
          particleCount: 45,
          pulseSpeed: 0.4
        },
        layers: {
          systemic: 0.5,
          collective: 0.4,
          personal: 0.1
        },
        duration: 4.0
      },
      {
        name: 'Legal Environment Shift',
        zone: 'liability',
        category: 'systemic',
        baseDamage: 15 * this.difficultyMultiplier,
        description: 'Regulatory change',
        visual: {
          color: '#E76F51',
          intensity: 0.5,
          particleCount: 40,
          pulseSpeed: 0.3
        },
        layers: {
          systemic: 0.7,
          collective: 0.2,
          personal: 0.1
        },
        duration: 6.0
      }
    ];
    
    return systemicEvents[Math.floor(Math.random() * systemicEvents.length)];
  }

  createShockEvent() {
    const shockEvents = [
      {
        name: 'Severe Accident',
        zone: 'auto',
        category: 'shock',
        baseDamage: 50 * this.difficultyMultiplier,
        description: 'Major collision',
        visual: {
          color: '#C1121F',
          intensity: 1.0,
          particleCount: 100,
          shockwave: true
        },
        layers: {
          systemic: 0.5,
          collective: 0.3,
          personal: 0.2     // Even good choices can't prevent all damage
        },
        duration: 3.0,
        isShock: true
      },
      {
        name: 'Major Storm',
        zone: 'home',
        category: 'shock',
        baseDamage: 60 * this.difficultyMultiplier,
        description: 'Hurricane or tornado',
        visual: {
          color: '#003049',
          intensity: 1.0,
          particleCount: 150,
          shockwave: true,
          screenShake: true
        },
        layers: {
          systemic: 0.6,
          collective: 0.3,
          personal: 0.1
        },
        duration: 4.0,
        isShock: true
      },
      {
        name: 'Acute Health Crisis',
        zone: 'health',
        category: 'shock',
        baseDamage: 55 * this.difficultyMultiplier,
        description: 'Emergency medical event',
        visual: {
          color: '#D62828',
          intensity: 1.0,
          particleCount: 120,
          shockwave: true
        },
        layers: {
          systemic: 0.5,
          collective: 0.2,
          personal: 0.3
        },
        duration: 3.5,
        isShock: true
      }
    ];
    
    return shockEvents[Math.floor(Math.random() * shockEvents.length)];
  }

  scheduleNextEvent() {
    // Calculate next event time based on frequencies
    const totalFrequency = Object.values(this.frequencies).reduce((a, b) => a + b, 0);
    const avgInterval = 60 / totalFrequency; // Convert to seconds
    
    // Add randomness (±30%)
    const variance = avgInterval * 0.3;
    const nextInterval = avgInterval + (Math.random() - 0.5) * 2 * variance;
    
    this.nextEventTime = this.currentTime + nextInterval;
  }

  selectRandomLives(lives, count) {
    if (lives.length === 0) return [];
    
    const shuffled = [...lives].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, lives.length));
  }

  getQueuedEvents() {
    return this.eventQueue.map(event => ({
      ...event,
      telegraphProgress: 1 - (event.timeUntilImpact / 3.0) // 0 to 1
    }));
  }

  // Manual event injection (for testing or tutorial)
  injectEvent(eventType, targetLife, delay = 3.0) {
    let event;
    
    if (eventType === 'routine') {
      event = this.createRoutineEvent();
    } else if (eventType === 'systemic') {
      event = this.createSystemicEvent();
    } else if (eventType === 'shock') {
      event = this.createShockEvent();
    } else {
      return;
    }
    
    this.eventQueue.push({
      ...event,
      id: `manual-${Date.now()}`,
      targetLife,
      timeUntilImpact: delay,
      createdAt: this.currentTime
    });
  }
}

